import React, { useState, useEffect, useCallback, useRef } from "react";
import uploadFile from "../../../../assets/VZY/upload-file.svg";
import axios from "axios";
import likeIcon from "../../../../assets/VZY/afterLike.svg";
import { useSearchParams } from "react-router-dom";
export const FeedBackModal = ({
  isOpen,
  onClose,
  onSubmitSuccess,
  selectedCategory,
}) => {
  const [form, setForm] = useState({
    email: "",
    subject: "",
    description: "",
    file: null,
    category: "",
  });

  const [errors, setErrors] = useState({});
  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategoryItemsValue, setSelectedCategoryItemsValue] =
    useState("");
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const apiBaseURL = process.env.REACT_APP_API_URL || window.location.origin;
  const toastTimerRef = useRef(null);
  const hasFetchedRef = useRef(false);
  const [searchParams] = useSearchParams();
  const REDIRECT_URL = "https://vzy.one/en/account";
  useEffect(() => {
    const urlToken = searchParams.get("a_t");
    const urlSid = searchParams.get("sid");
    const urlMno = searchParams.get("mno");

    const storedData = JSON.parse(
      sessionStorage.getItem("helpSupportData") || "{}",
    );
    if (urlToken && urlSid && urlMno) {
      sessionStorage.setItem(
        "helpSupportData",
        JSON.stringify({
          token: urlToken,
          sid: urlSid,
          mno: urlMno,
        }),
      );
      return;
    }
    const isTokenEmpty = !storedData?.token?.trim();
    const isSidEmpty = !storedData?.sid?.trim();
    const isSidZero = Number(storedData?.sid?.trim()) === 0;
    const isMnoEmpty = !storedData?.mno?.trim();
    if (isTokenEmpty || isSidEmpty || isMnoEmpty || isSidZero) {
      window.location.replace(REDIRECT_URL);
    }
  }, [searchParams]);

  const showToastMessage = useCallback((message, duration = 3000) => {
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }
    setToastMessage(message);
    setShowToast(true);

    toastTimerRef.current = setTimeout(() => {
      setShowToast(false);
      setToastMessage("");
    }, duration);
  }, []);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        clearTimeout(toastTimerRef.current);
      }
      document.body.style.overflow = "";
    };
  }, []);

  const resetForm = useCallback(() => {
    setForm({
      email: "",
      subject: "",
      description: "",
      file: null,
      category: "",
    });
    setErrors({});
    setSelectedCategoryItemsValue("");
  }, []);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";

    if (isOpen) {
      resetForm();
      hasFetchedRef.current = false;
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen, resetForm]);

  const fetchFilterList = useCallback(async () => {
    if (hasFetchedRef.current || loading) {
      return;
    }

    hasFetchedRef.current = true;
    setLoading(true);
    const payload = { ContentTypeID: 0 };

    try {
      const response = await axios.post(
        `${apiBaseURL}/api/VZYHelpSupport/FetchFilterCategory`,
        payload,
        {
          headers: { "Content-Type": "application/json" },
          timeout: 10000,
        },
      );

      const fetchedCategories = response?.data?.result?.filterCategories;

      if (Array.isArray(fetchedCategories) && fetchedCategories.length > 0) {
        const uniqueCategories = Array.from(
          new Map(
            fetchedCategories
              .filter((item) => item && item.categoryName)
              .map((item) => [item.categoryName.trim().toLowerCase(), item]),
          ).values(),
        );
        setCategories(uniqueCategories);
      } else {
        setCategories([]);
        console.warn("No categories returned from API");
      }
    } catch (err) {
      console.error("FetchFilterCategory error:", err);
      setCategories([]);
      hasFetchedRef.current = false;
      showToastMessage("Failed to load categories. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [apiBaseURL, showToastMessage, loading]);

  useEffect(() => {
    if (isOpen && categories.length === 0 && !hasFetchedRef.current) {
      fetchFilterList();
    }
  }, [isOpen, categories.length, fetchFilterList]);

  useEffect(() => {
    if (selectedCategory && categories.length > 0) {
      const match = categories.find(
        (item) =>
          item.categoryName?.toLowerCase().trim() ===
          selectedCategory?.toLowerCase().trim(),
      );

      if (match) {
        setForm((prev) => ({
          ...prev,
          category: String(match.categoryID),
        }));
        setSelectedCategoryItemsValue(match.categoryName);
      }
    }
  }, [selectedCategory, categories]);

  const validateAndSetFile = useCallback((file) => {
    if (!file) {
      setErrors((prev) => ({ ...prev, file: null }));
      setForm((prev) => ({ ...prev, file: null }));
      return;
    }

    const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];
    const maxSize = 250 * 1024;

    if (!allowedTypes.includes(file.type)) {
      setErrors((prev) => ({
        ...prev,
        file: "Only JPG and PNG images are allowed",
      }));
      setForm((prev) => ({ ...prev, file: null }));
      return;
    }

    if (file.size > maxSize) {
      setErrors((prev) => ({
        ...prev,
        file: `File must be under 250KB (current: ${Math.round(file.size / 1024)}KB)`,
      }));
      setForm((prev) => ({ ...prev, file: null }));
      return;
    }

    setErrors((prev) => ({ ...prev, file: null }));
    setForm((prev) => ({ ...prev, file }));
  }, []);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));

    setErrors((prev) => {
      if (prev[name]) {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      }
      return prev;
    });
  }, []);

  const handleFileBrowse = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      validateAndSetFile(file);
    },
    [validateAndSetFile],
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      const file = e.dataTransfer.files?.[0];
      validateAndSetFile(file);
    },
    [validateAndSetFile],
  );

  const handleRemoveFile = useCallback(() => {
    setForm((prev) => ({ ...prev, file: null }));
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.file;
      return newErrors;
    });
  }, []);

  const validate = useCallback(() => {
    const newErrors = {};

    if (!form.category) {
      newErrors.category = "Category is required";
    }

    if (!form.email) {
      newErrors.email = "Email is required";
    } else if (form.email.trim().length > 50) {
      newErrors.email = "Email must be less than 50 characters";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      newErrors.email = "Invalid email format";
    }

    if (!form.subject) {
      newErrors.subject = "Subject is required";
    } else if (form.subject.trim().length < 5) {
      newErrors.subject = "Subject must be at least 5 characters";
    } else if (form.subject.trim().length > 50) {
      newErrors.subject = "Subject must be less than 50 characters";
    }

    if (!form.description) {
      newErrors.description = "Description is required";
    } else if (form.description.trim().length < 10) {
      newErrors.description = "Description must be at least 10 characters";
    } else if (form.description.trim().length > 500) {
      newErrors.description = "Description must be less than 500 characters";
    }

    if (errors.file) {
      newErrors.file = errors.file;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [form, errors.file]);

  const handleCancel = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const getSessionData = useCallback(() => {
    try {
      const data = sessionStorage.getItem("helpSupportData");
      return data ? JSON.parse(data) : {};
    } catch (error) {
      console.error("Error parsing session data:", error);
      return {};
    }
  }, []);

  const isSessionInvalid = () => {
    try {
      const storedData = JSON.parse(
        sessionStorage.getItem("helpSupportData") || "{}",
      );
      const isTokenEmpty = !storedData?.token?.trim();
      const isSidEmpty = !storedData?.sid?.trim();
      const isSidZero = Number(storedData?.sid?.trim()) === 0;
      const isMnoEmpty = !storedData?.mno?.trim();

      return isTokenEmpty || isSidEmpty || isMnoEmpty || isSidZero;
    } catch {
      return true;
    }
  };

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (isSessionInvalid()) {
        window.location.replace(REDIRECT_URL);
        return;
      }
      if (!validate()) {
        return;
      }

      if (isSubmitting) {
        return;
      }

      setIsSubmitting(true);

      try {
        const sessionData = getSessionData();
        const formData = new FormData();
        formData.append("OttSMSId", sessionData.sid || 0);
        formData.append("Category", selectedCategoryItemsValue || "");
        formData.append("SubCategory", form.subject.trim());
        formData.append("MailForm", form.email.trim());
        formData.append("MailId", form.email.trim());
        formData.append("Subject", form.subject.trim());
        formData.append("ComplainDetails", form.description.trim());
        formData.append("LinkURL", window.location.href);
        formData.append("CategoryId", form.category || 1);
        formData.append("ParentCategoryId", "");
        formData.append("SMSToken", sessionData.token || "");

        if (form.file) {
          formData.append("File", form.file);
        }
        const response = await axios.post(
          `${apiBaseURL}/api/v1/ContactUs/VZYComplainAndFeedback`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 30000,
          },
        );

        if (response?.data?.resultStatus === "Success") {
          resetForm();
          const message =
            response?.data?.resultMessage || "Ticket raised successfully!";
          showToastMessage(message);
          setTimeout(async () => {
            onClose();
            if (onSubmitSuccess) {
              await onSubmitSuccess();
            }
          }, 1500);
        } else {
          const errorMessage =
            response?.data?.resultMessage ||
            "Failed to submit ticket. Please try again.";
          showToastMessage(errorMessage);
        }
      } catch (error) {
        console.error("Submit error:", error);
        let errorMessage = "Something went wrong. Please try again.";
        if (error.code === "ECONNABORTED") {
          errorMessage = "Request timed out. Please check your connection.";
        } else if (error.response) {
          errorMessage =
            error.response.data?.resultMessage ||
            `Error: ${error.response.status}`;
        } else if (error.request) {
          errorMessage =
            "No response from server. Please check your connection.";
        }

        showToastMessage(errorMessage);
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      validate,
      isSubmitting,
      getSessionData,
      selectedCategoryItemsValue,
      form,
      apiBaseURL,
      showToastMessage,
      resetForm,
      onClose,
      onSubmitSuccess,
    ],
  );

  const handleCategoryChange = useCallback(
    (e) => {
      handleChange(e);

      const selected = categories.find(
        (c) => String(c.categoryID) === e.target.value,
      );

      if (selected) {
        setSelectedCategoryItemsValue(selected.categoryName);
      } else {
        setSelectedCategoryItemsValue("");
      }
    },
    [categories, handleChange],
  );

  if (!isOpen) return null;

  return (
    <>
      {showToast && (
        <div className="feedback-toast-success">
          <img src={likeIcon} alt="Success" />
          {toastMessage}
        </div>
      )}

      <div className="feedback-modal" role="dialog" aria-modal="true">
        <div className="modal-form-box">
          <div className="feedback-modal-content">
            <button
              className="closeModal-btn"
              onClick={onClose}
              aria-label="Close modal"
              disabled={isSubmitting}
            >
              ×
            </button>

            <h4>Raise a ticket</h4>

            <form onSubmit={handleSubmit}>
              <div className="feedback-row">
                <div className="feedback-col1">
                  <input
                    type="email"
                    name="email"
                    placeholder="Email ID*"
                    value={form.email}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    autoComplete="email"
                  />
                  {errors.email && <p className="error-text">{errors.email}</p>}
                </div>

                <div className="feedback-col1">
                  <input
                    type="text"
                    name="subject"
                    placeholder="Subject*"
                    value={form.subject}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    maxLength={200}
                  />
                  {errors.subject && (
                    <p className="error-text">{errors.subject}</p>
                  )}
                </div>

                <div className="feedback-col2">
                  <select
                    name="category"
                    value={form.category}
                    onChange={handleCategoryChange}
                    disabled={isSubmitting || loading}
                  >
                    <option value="">
                      {loading ? "Loading categories..." : "Select Category*"}
                    </option>
                    {categories.map((item) => (
                      <option
                        key={item.categoryID}
                        value={String(item.categoryID)}
                      >
                        {item.categoryName}
                      </option>
                    ))}
                  </select>
                  {errors.category && (
                    <p className="error-text">{errors.category}</p>
                  )}
                </div>

                <div className="feedback-col2">
                  <textarea
                    name="description"
                    placeholder="Description*"
                    value={form.description}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    maxLength={1000}
                    rows={5}
                  />
                  {errors.description && (
                    <p className="error-text">{errors.description}</p>
                  )}
                </div>

                <div
                  className="file-drop-zone"
                  onDrop={handleDrop}
                  onDragOver={(e) => e.preventDefault()}
                >
                  <div>
                    {form.file ? (
                      <div className="file-uploaded">
                        <p className="file-name">{form.file.name}</p>
                        <button
                          type="button"
                          onClick={handleRemoveFile}
                          className="remove-file-btn"
                          disabled={isSubmitting}
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="upload-icon">
                          <img src={uploadFile} alt="Upload" />
                        </div>
                        <label className="browse-link">
                          Browse
                          <input
                            type="file"
                            hidden
                            accept="image/png,image/jpeg,image/jpg"
                            onChange={handleFileBrowse}
                            disabled={isSubmitting}
                          />
                        </label>
                        <span> or Drag files here</span>
                        <p className="file-hint">Max 250KB (JPG, PNG only)</p>
                      </>
                    )}
                    {errors.file && <p className="error-text">{errors.file}</p>}
                  </div>
                </div>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="modal-cancel"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="modal-submit"
                  disabled={isSubmitting || loading}
                >
                  {isSubmitting ? "Submitting..." : "Submit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};
